<script setup lang="ts">
import { TABLE_TYPE } from "./useTable";
import { useTableMain } from "./useTableMain";
import type { TableProps } from "./TableProps";

/**
 * **Table** (`e-table`)
 *
 * Root table component that provides structure and state for all child
 * table components (`TableHead`, `TableBody`, `TableFooter`, `TableRow`,
 * `TableHeaderCell`, `TableCell`).
 *
 * Manages column widths, row selection, sorting, and scroll behavior
 * centrally via the `useTable` composable and the shared stateStore.
 *
 * @example
 * ```html
 * <!-- Basic table -->
 * <e-table>
 *   <e-table-head>
 *     <e-table-row>
 *       <e-table-header-cell>Name</e-table-header-cell>
 *       <e-table-header-cell>Age</e-table-header-cell>
 *     </e-table-row>
 *   </e-table-head>
 *   <e-table-body>
 *     <e-table-row>
 *       <e-table-cell>Alice</e-table-cell>
 *       <e-table-cell>30</e-table-cell>
 *     </e-table-row>
 *   </e-table-body>
 * </e-table>
 *
 * <!-- Sortable table with single row selection -->
 * <e-table sortable selectable="single">
 *   <!-- ... -->
 * </e-table>
 *
 * <!-- Resizable columns with vertical scrolling -->
 * <e-table column-sizing="resizable" scroll="vertical" max-height="400px">
 *   <!-- ... -->
 * </e-table>
 * ```
 */
defineOptions({
  name: TABLE_TYPE,
});

const props = withDefaults(defineProps<TableProps>(), {
  color: "neutral",
  variant: "transparent",
  size: "md",
  striped: true,
  columnSizing: "content",
  selectedRows: "[]",
  scroll: undefined,
  maxHeight: undefined,
});

const { classes, columnWidthStyle } = useTableMain({ props });
</script>

<style>
:host {
  display: block;
}
</style>

<template>
  <div part="table" :class="classes" :style="columnWidthStyle">
    <slot />
  </div>
</template>
